/* hom: hom-pdp-media.js (House of Maryam layer) — product page gallery (Zenith 1.5.3).
   - Photo counter on phones ("3 / 12"), read from Zenith's Swiper on .js-media-list (el.swiper, Swiper 11).
   - Share: the phone's share sheet (navigator.share) or copy the message, "Zarposh | Inara, 160 GBP, <link>"
     (theme setting hom_share_text; data in the snippet's data-hom-share-data block, read at the moment of sharing).
   - Save photos: watermarked copies drawn on a canvas, a brand band added below each photo (logo, HOUSE OF MARYAM,
     www.houseofmaryam.com, designer and design name), saved one by one (share sheet on phones, with the same
     message; downloads elsewhere).
   Markup: snippets/hom-media-overlay.liquid, snippets/hom-photo-tools.liquid. Styles: assets/hom-pdp-media.css.
   Loaded with defer from snippets/hom-head.liquid, before main-product.js creates the Swiper, so the counter waits for it.
   Zenith's files are never changed; everything here only reads Zenith's DOM and Swiper. */
(() => {
  if (window.HOMPdpMedia) return;
  const api = (window.HOMPdpMedia = {});

  const OVERLAY = '[data-hom-media-overlay]';
  const QUICK_VIEW = 'quick-view-modal';

  const format = (template, values) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const debounce = (fn, ms) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), ms);
    };
  };

  const inQuickView = (el) => Boolean(el && el.closest(QUICK_VIEW));

  const readJSON = (el) => {
    try {
      return JSON.parse(el ? el.textContent : '{}') || {};
    } catch (error) {
      return {};
    }
  };

  /* ------------------------------------------------------------------
     Photo counter
     ------------------------------------------------------------------ */
  const counters = new WeakMap();

  function initCounter(overlay) {
    const counter = overlay.querySelector('[data-hom-media-counter]');
    if (!counter || counters.has(counter)) return;

    const wrapper = overlay.closest('.product__media-list-wrapper');
    const list = wrapper && wrapper.querySelector('.js-media-list');
    if (!list) return;

    // Only the visible chip is shown or hidden; the spoken live region stays rendered so screen readers register it
    // on load, and it already holds "Photo 1 of N" from the server, so the first update below is silent.
    const visible = counter.querySelector('[data-hom-counter-visible]');
    const spoken = counter.querySelector('[data-hom-counter-text]');
    const showChip = (show) => {
      if (visible) visible.hidden = !show;
    };
    const state = { swiper: null };
    counters.set(counter, state);

    // Real photos only: Swiper's loop mode may add duplicate or blank slides.
    const total = () =>
      Array.from(list.querySelectorAll('.swiper-wrapper > .swiper-slide')).filter(
        (slide) => !slide.classList.contains('swiper-slide-duplicate') && !slide.classList.contains('swiper-slide-blank')
      ).length;

    const update = () => {
      const swiper = state.swiper;
      const count = total();
      if (!swiper || swiper.destroyed || count < 2) {
        showChip(false);
        return;
      }
      const raw = typeof swiper.realIndex === 'number' ? swiper.realIndex : swiper.activeIndex;
      const current = Math.min(Math.max(Number(raw) || 0, 0), count - 1) + 1;
      const text = `${current} / ${count}`;
      const words = format(counter.dataset.textTemplate, { n: current, total: count });
      if (visible && visible.textContent !== text) visible.textContent = text;
      if (spoken && spoken.textContent !== words) spoken.textContent = words;
      showChip(true);
    };

    const attach = () => {
      const swiper = list.swiper;
      if (!swiper || swiper.destroyed) {
        showChip(false);
        return false;
      }
      if (swiper !== state.swiper) {
        state.swiper = swiper;
        swiper.on('slideChange', update);
        swiper.on('realIndexChange', update);
        swiper.on('destroy', () => {
          if (state.swiper === swiper) state.swiper = null;
          showChip(false);
        });
      }
      update();
      return true;
    };

    // main-product.js creates the Swiper after this file runs (and sometimes from a ResizeObserver), so wait briefly.
    let tries = 0;
    const poll = () => {
      if (!counter.isConnected || attach() || ++tries > 60) return;
      setTimeout(poll, 150);
    };
    poll();

    // Zenith can rebuild its Swiper when the window crosses the 990px breakpoint.
    const onResize = debounce(() => {
      if (!counter.isConnected) {
        window.removeEventListener('resize', onResize);
        return;
      }
      attach();
    }, 250);
    window.addEventListener('resize', onResize);
  }

  /* ------------------------------------------------------------------
     Share
     ------------------------------------------------------------------ */
  const toastTimers = new WeakMap();

  function notify(tools, message) {
    if (!tools || !message) return;
    const live = tools.querySelector('[data-hom-tools-live]');
    const toast = tools.querySelector('[data-hom-tools-toast]');
    if (live) {
      live.textContent = '';
      setTimeout(() => {
        live.textContent = message;
      }, 60);
    }
    if (toast) {
      toast.textContent = message;
      toast.hidden = false;
      clearTimeout(toastTimers.get(toast));
      toastTimers.set(
        toast,
        setTimeout(() => {
          toast.hidden = true;
          if (live) live.textContent = '';
        }, 2600)
      );
    }
  }

  const addressVariant = () => {
    const variant = new URLSearchParams(window.location.search).get('variant');
    return variant && /^\d+$/.test(variant) ? variant : '';
  };

  function shareUrl(base) {
    let url;
    try {
      url = new URL(base || window.location.pathname, window.location.origin);
    } catch (error) {
      return window.location.href;
    }
    // Keep the chosen variant (Zenith writes ?variant= into the address bar), drop everything else.
    url.search = '';
    url.hash = '';
    const variant = addressVariant();
    if (variant) url.searchParams.set('variant', variant);
    return url.toString();
  }

  // The variant in Zenith's product form right now (it keeps input[name="id"] current), else the address bar's.
  function currentVariantId(formId) {
    const form = formId ? document.getElementById(formId) : null;
    const input = form && form.querySelector('input[name="id"]');
    if (input && /^\d+$/.test(input.value)) return input.value;
    return addressVariant();
  }

  // Placeholders that come out empty (a missing price) must not leave ", ," or a trailing comma behind:
  // a run of separators keeps only its last one. No-break spaces (inside "160 GBP") are kept.
  const tidy = (text) =>
    String(text || '')
      .replace(/(?:[ \t]*[,;|])+(?=[ \t]*[,;|])/g, '')
      .replace(/[ \t]+([,;])/g, '$1')
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/^[\s,;:|]+|[\s,;:|]+$/g, '');

  /**
   * The share message for the product around `el`, built now so it follows the chosen variant.
   * @returns {{ title: string, url: string, message: string, full: string, photoText: boolean }}
   *   message: without the link (share sheets add `url` themselves); full: with the link (copy, photos).
   */
  function shareDetails(el) {
    const scope = (el && el.closest(OVERLAY)) || document;
    const data = readJSON(scope.querySelector('[data-hom-share-data]'));
    const url = shareUrl(data.url);
    const variant = currentVariantId(data.form);
    const price = (variant && data.prices && data.prices[variant]) || data.price || '';
    const values = {
      title: data.title || document.title,
      name: data.name || data.title || '',
      designer: data.designer || '',
      design: data.design || '',
      price,
      url,
    };
    const template = String(data.template || '[name], [price], [url]');
    let full = tidy(format(template, values));
    if (!template.includes('[url]')) full = full ? `${full} ${url}` : url;
    return {
      title: values.title,
      url,
      message: tidy(format(template.split('[url]').join(''), values)),
      full,
      photoText: data.photoText !== false,
    };
  }

  async function copyText(text, returnFocusTo) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (error) {
      /* fall back below */
    }
    let ok = false;
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;';
    document.body.appendChild(area);
    try {
      area.select();
      ok = document.execCommand('copy');
    } catch (error) {
      ok = false;
    }
    area.remove();
    if (returnFocusTo) returnFocusTo.focus({ preventScroll: true });
    return ok;
  }

  async function onShare(button) {
    const tools = button.closest('[data-hom-photo-tools]');
    const details = shareDetails(button);
    if (navigator.share) {
      // The message without the link plus `url`: share sheets join the two themselves ("… 160 GBP https://…"),
      // and apps that only take a link (Facebook) still get it.
      const data = { title: details.title, url: details.url };
      if (details.message) data.text = details.message;
      try {
        await navigator.share(data);
        return;
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        // Anything else (not allowed, unsupported data): copy the message instead.
      }
    }
    const copied = await copyText(details.full, button);
    notify(tools, copied ? button.dataset.textCopied : button.dataset.textError);
  }

  /* ------------------------------------------------------------------
     Watermark
     ------------------------------------------------------------------ */
  const MAX_WIDTH = 1600;

  // The band's fonts, loaded once from the files the snippet names, under their own family names (the page's
  // "HoM Trajan" faces are size-adjusted for headings). Trajan's files map the middle dot (U+00B7) to an empty
  // glyph, so its faces leave that one character to the fallback font.
  const FACES = {
    brand: { family: 'HoM Watermark', fallback: '"Times New Roman", Times, serif' },
    sans: { family: 'HoM Watermark Sans', fallback: '"Helvetica Neue", Arial, sans-serif' },
    italic: { family: 'HoM Watermark Italic', fallback: 'Georgia, "Times New Roman", serif' },
  };
  const TRAJAN_RANGE = 'U+0000-00B6, U+00B8-10FFFF';
  const faceLoads = new Map();

  function loadFace(face, url, descriptors) {
    if (!url || typeof FontFace === 'undefined' || !document.fonts) return Promise.resolve();
    const key = `${face.family}|${descriptors.style || 'normal'}|${descriptors.weight}`;
    if (!faceLoads.has(key)) {
      let loading;
      try {
        const font = new FontFace(face.family, `url("${String(url).replace(/["\\\s]/g, '')}")`, descriptors);
        loading = font
          .load()
          .then((loaded) => {
            document.fonts.add(loaded);
          })
          .catch(() => {});
      } catch (error) {
        loading = Promise.resolve();
      }
      faceLoads.set(key, loading);
    }
    return faceLoads.get(key);
  }

  // Waits 3 seconds at most: a late font is drawn with its fallback rather than holding up the photos.
  function loadFonts(files) {
    const fonts = files || {};
    const loading = Promise.all([
      loadFace(FACES.brand, fonts.semibold, { weight: '600', style: 'normal', unicodeRange: TRAJAN_RANGE }),
      loadFace(FACES.brand, fonts.regular, { weight: '400', style: 'normal', unicodeRange: TRAJAN_RANGE }),
      loadFace(FACES.sans, fonts.sans, { weight: '400', style: 'normal' }),
      loadFace(FACES.italic, fonts.italic, { weight: '400', style: 'italic' }),
    ]);
    return Promise.race([loading, wait(3000)]);
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      // A photo that never arrives must not block the others: give up after 20 seconds.
      const timer = setTimeout(() => reject(new Error('timeout')), 20000);
      img.crossOrigin = 'anonymous'; // Shopify's CDN answers with Access-Control-Allow-Origin: *, so the canvas stays exportable.
      img.decoding = 'async';
      img.onload = () => {
        clearTimeout(timer);
        resolve(img);
      };
      img.onerror = () => {
        clearTimeout(timer);
        reject(new Error('image'));
      };
      img.src = src;
    });
  }

  function canvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('blob'))), 'image/jpeg', 0.9);
      } catch (error) {
        reject(error);
      }
    });
  }

  async function drawImage(src) {
    try {
      return await loadImage(src);
    } catch (error) {
      // A copy cached earlier without CORS can fail in some browsers: ask for a fresh one.
      return loadImage(`${src}${src.includes('?') ? '&' : '?'}hom-wm=1`);
    }
  }

  /* A line of text in the band: { text, face, weight, style, size (px), min (px), tracking (em) }.
     It may shrink to `share` of its planned size, never below `floor` px, before it is shortened. */
  const bandLine = (text, face, size, floor, share, extra) =>
    Object.assign({ text, face, weight: 400, style: 'normal', size, min: Math.max(floor, size * share) }, extra);

  function setFont(ctx, line, size) {
    const family = `"${line.face.family}", ${line.face.fallback}`;
    ctx.font = `${line.style || 'normal'} ${line.weight || 400} ${Math.max(1, Math.round(size))}px ${family}`;
  }

  const lettersOf = (text) => Array.from(text);

  function spacedWidth(ctx, text, tracking) {
    return ctx.measureText(text).width + tracking * Math.max(0, lettersOf(text).length - 1);
  }

  // Letter spacing is drawn one letter at a time (ctx.letterSpacing is too new for older iPhones). Each letter
  // starts where the text before it ends, so the font's kerning is kept.
  function drawSpaced(ctx, text, x, y, tracking, align) {
    const width = spacedWidth(ctx, text, tracking);
    let start = x;
    if (align === 'center') start = x - width / 2;
    if (align === 'end') start = x - width;
    ctx.textAlign = 'left';
    if (!tracking) {
      ctx.fillText(text, start, y);
      return;
    }
    let before = '';
    lettersOf(text).forEach((letter, index) => {
      ctx.fillText(letter, start + ctx.measureText(before).width + tracking * index, y);
      before += letter;
    });
  }

  // Fit a line into `room`: smaller first (down to its minimum), then shortened with an ellipsis.
  // Returns the line with its final text, size, letter spacing in px (px) and width.
  function fitLine(ctx, line, room) {
    let size = line.size;
    const tracking = () => size * (line.tracking || 0);
    setFont(ctx, line, size);
    while (size > line.min && spacedWidth(ctx, line.text, tracking()) > room) {
      size = Math.max(line.min, size - 1);
      setFont(ctx, line, size);
    }
    let text = line.text;
    let kept = 0; // letters kept when the line had to be shortened
    if (spacedWidth(ctx, text, tracking()) > room) {
      const letters = lettersOf(text);
      while (letters.length > 1 && spacedWidth(ctx, `${letters.join('').trimEnd()}…`, tracking()) > room) {
        letters.pop();
      }
      // End on a whole word when that still leaves a few letters ("Embroidered…", not "Embroidered O…").
      let short = letters.join('').trimEnd();
      const space = short.lastIndexOf(' ');
      if (space >= 6) short = short.slice(0, space).replace(/[\s,;:|·-]+$/, '');
      kept = short.trim().length;
      text = `${short}…`;
    }
    return Object.assign({}, line, { text, size, kept, px: tracking(), width: spacedWidth(ctx, text, tracking()) });
  }

  // Ink above and below the baseline (typical values where the browser cannot measure them).
  function inkOf(ctx, line) {
    setFont(ctx, line, line.size);
    const metrics = ctx.measureText(line.text);
    const up = Number.isFinite(metrics.actualBoundingBoxAscent) ? metrics.actualBoundingBoxAscent : line.size * 0.7;
    const down = Number.isFinite(metrics.actualBoundingBoxDescent) ? metrics.actualBoundingBoxDescent : line.size * 0.2;
    return { up: Math.max(0, up), down: Math.max(0, down) };
  }

  // Baselines for lines stacked in the middle of the band (top to bottom), with `gap` px of air between them.
  function stack(ctx, lines, top, height, gap) {
    const inks = lines.map((line) => inkOf(ctx, line));
    const block = inks.reduce((sum, ink, index) => sum + ink.up + (index ? gap : 0), 0) + inks[inks.length - 1].down;
    const start = top + (height - block) / 2;
    const baselines = [];
    inks.forEach((ink, index) => {
      baselines.push(index ? baselines[index - 1] + gap + ink.up : start + ink.up);
    });
    return { top: start, block, baselines };
  }

  /**
   * Draw the photo (max 1600px wide) and add a band BELOW it, so the outfit is never covered: the brand's teal
   * (the scheme's button colour) under a thin gold line, then the logo with the name in Trajan over the web address
   * on one side, and the designer over the design name on the other. When the second column does not fit, the
   * logo, name and address are centred on their own. Sizes follow the photo's shorter side, so the address reads
   * on a phone screen (about 12px when a 700px photo fills a 390px screen) and nothing overflows on tiny photos.
   * @returns {Promise<Blob>} JPEG
   */
  async function watermark(src, opts) {
    const img = await drawImage(src);
    const scale = Math.min(1, MAX_WIDTH / (img.naturalWidth || MAX_WIDTH));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const u = Math.min(w, h);
    const bandH = Math.max(48, Math.round(u * opts.bandRatio));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h + bandH;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.textBaseline = 'alphabetic';
    if ('direction' in ctx) ctx.direction = 'ltr';
    ctx.fillStyle = `rgb(${opts.bg})`;
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    // The band and its thin gold line along the photo's edge.
    const hair = Math.max(1, Math.round(u * 0.003));
    ctx.fillStyle = `rgb(${opts.band})`;
    ctx.fillRect(0, h, w, bandH);
    const gold = ctx.createLinearGradient(0, 0, w, 0);
    gold.addColorStop(0, `rgb(${opts.goldA})`);
    gold.addColorStop(1, `rgb(${opts.goldB})`);
    ctx.fillStyle = gold;
    ctx.fillRect(0, h, w, hair);

    const padX = Math.max(10, Math.round(u * 0.045));
    const inner = w - padX * 2;
    const top = h + hair;
    const height = bandH - hair;

    // Left (or centred): the name in spaced Trajan capitals over the web address in the clear sans serif.
    const left = [
      bandLine(opts.brand, FACES.brand, Math.max(13, u * 0.038), 9, 0.7, { weight: 600, tracking: 0.12 }),
      bandLine(opts.site, FACES.sans, Math.max(11, u * 0.031), 8, 0.7, { tracking: 0.02 }),
    ]
      .filter((entry) => entry.text)
      .map((entry) => fitLine(ctx, entry, inner));
    if (!left.length) return finish(canvas);

    const gap = Math.max(4, left[left.length - 1].size * 0.62);
    const leftStack = stack(ctx, left, top, height, gap);
    const textW = Math.max(...left.map((line) => line.width));

    // The logo (the gold HM monogram) as tall as the two lines, when it fits.
    let logoW = 0;
    let logoH = 0;
    const logoGap = Math.max(6, Math.round(u * 0.03));
    const logo = opts.logo;
    if (logo && logo.naturalWidth && logo.naturalHeight) {
      logoH = Math.min(leftStack.block * 1.12, height - 8);
      logoW = (logoH * logo.naturalWidth) / logo.naturalHeight;
      if (logoW + logoGap + textW > inner) logoW = 0;
    }
    const groupW = textW + (logoW ? logoW + logoGap : 0);

    // Right: the designer (Trajan, small capitals) over the design name (italic), at most 20% smaller than
    // planned, then shortened. Left out when a line would keep fewer than 8 letters: then the left side is centred.
    const gapX = Math.max(padX, Math.round(u * 0.06));
    const room = inner - groupW - gapX;
    let right = [
      bandLine(opts.designer, FACES.brand, Math.max(11, u * 0.027), 9, 0.8, { tracking: 0.06 }),
      bandLine(opts.design, FACES.italic, Math.max(12, u * 0.033), 10, 0.8, { style: 'italic' }),
    ]
      .filter((entry) => entry.text)
      .map((entry) => fitLine(ctx, entry, Math.max(1, room)));
    if (room < u * 0.16 || right.some((entry) => entry.kept && entry.kept < 8)) right = [];

    const groupX = right.length ? padX : Math.round((w - groupW) / 2);
    const ink = `rgb(${opts.ink})`;

    if (logoW) {
      ctx.drawImage(logo, groupX, leftStack.top + leftStack.block / 2 - logoH / 2, logoW, logoH);
    }
    // Lines start next to the logo; with neither logo nor right column, each line is centred on its own.
    const centred = !right.length && !logoW;
    const textX = groupX + (logoW ? logoW + logoGap : 0);
    ctx.fillStyle = ink;
    left.forEach((line, index) => {
      setFont(ctx, line, line.size);
      drawSpaced(ctx, line.text, centred ? w / 2 : textX, leftStack.baselines[index], line.px, centred ? 'center' : 'start');
    });

    if (right.length) {
      // Same baselines as the left lines when both sides have two; a single line sits in the middle.
      const baselines =
        right.length === left.length ? leftStack.baselines : stack(ctx, right, top, height, gap).baselines;
      ctx.fillStyle = `rgba(${opts.ink}, 0.85)`;
      right.forEach((line, index) => {
        setFont(ctx, line, line.size);
        drawSpaced(ctx, line.text, w - padX, baselines[index], line.px, 'end');
      });
    }

    return finish(canvas);
  }

  async function finish(canvas) {
    const blob = await canvasToBlob(canvas); // throws if the canvas were ever tainted
    canvas.width = 0;
    canvas.height = 0;
    return blob;
  }

  /* ------------------------------------------------------------------
     Save photos panel
     ------------------------------------------------------------------ */
  const isTouch = () => window.matchMedia('(hover: none), (pointer: coarse)').matches;

  function makeFile(blob, name) {
    try {
      return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
    } catch (error) {
      return null;
    }
  }

  /**
   * Phones: the share sheet lets customers "Save image" to Photos, or send the photos on with the share message.
   * The link goes inside `text` (some apps drop `url` when files come with it) and no separate `url` is passed:
   * Android joins `text` and `url` into one message, so the link would appear twice. When the browser refuses
   * text with files (or the hom_share_photo_text setting is off), the photos go alone, as before.
   * Desktop: plain downloads.
   * @returns {object|null} the data for navigator.share, or null for downloads
   */
  function shareSheetData(files, details) {
    if (!isTouch() || !navigator.share || !navigator.canShare || !files.length || files.some((file) => !file)) {
      return null;
    }
    const title = details && details.title;
    const options = [];
    if (details && details.photoText && details.full) options.push({ files, title, text: details.full });
    options.push(title ? { files, title } : { files });
    for (const data of options) {
      try {
        if (navigator.canShare(data)) return data;
      } catch (error) {
        /* try the next */
      }
    }
    return null;
  }

  function download(result) {
    const link = document.createElement('a');
    link.href = result.url;
    link.download = result.name;
    link.rel = 'noopener';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class SavePhotos {
    constructor(dialog) {
      this.dialog = dialog;
      this.data = readJSON(dialog.querySelector('[data-hom-save-data]'));
      this.text = this.data.text || {};
      this.items = Array.from(dialog.querySelectorAll('[data-hom-save-item]'));
      this.saveAllButton = dialog.querySelector('[data-hom-save-all]');
      this.status = dialog.querySelector('[data-hom-save-status]');
      this.results = new Map();
      this.opener = null;
      this.preparing = null;
      this.saving = false;

      dialog.addEventListener('click', (event) => this.onClick(event));
      dialog.addEventListener('keydown', (event) => this.onKeydown(event));
      dialog.addEventListener('close', () => this.onClose());
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        this.dialog.classList.add('hom-save-dialog--fallback');
        this.dialog.setAttribute('open', '');
      }
      document.body.classList.add('overflow-hidden');
      const close = this.dialog.querySelector('[data-hom-save-close]');
      if (close) close.focus({ preventScroll: true });
      this.prepareAll();
    }

    close() {
      if (!this.dialog.open) return;
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.onClose();
      }
    }

    onClose() {
      document.body.classList.remove('overflow-hidden');
      if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
    }

    onClick(event) {
      // A click on the dialog box itself is a click on the backdrop around the panel.
      if (event.target === this.dialog) {
        this.close();
        return;
      }
      if (event.target.closest('[data-hom-save-close]')) {
        this.close();
        return;
      }
      const one = event.target.closest('[data-hom-save-one]');
      if (one) {
        this.saveOne(one.closest('[data-hom-save-item]'));
        return;
      }
      if (event.target.closest('[data-hom-save-all]')) this.saveAll();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && typeof this.dialog.showModal !== 'function') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      // Keep Tab cycling inside the panel.
      const focusable = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.getClientRects().length > 0
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message || '';
    }

    // Band colours from the panel's colour scheme: the button colour (brand teal) and its text colour (white),
    // the photo's backdrop for transparent images, and the brand golds for the thin line.
    renderOptions() {
      const styles = getComputedStyle(this.dialog);
      const read = (name, fallback) => styles.getPropertyValue(name).trim() || fallback;
      return {
        bg: read('--color-background', '255, 255, 255'),
        band: read('--color-button', '7, 38, 40'),
        ink: read('--color-button-text', '255, 255, 255'),
        goldA: read('--hom-gold-a', '173, 111, 55'),
        goldB: read('--hom-gold-b', '242, 184, 114'),
        bandRatio: Number(this.data.band) > 0 ? Number(this.data.band) : 0.15,
        brand: String(this.data.brand || '').trim(),
        site: String(this.data.site || '').trim(),
        designer: String(this.data.designer || '').trim(),
        design: String(this.data.design || '').trim(),
        logo: null,
      };
    }

    // The logo, once per panel; a logo that fails or is slow is simply left out.
    loadLogo() {
      if (!this.logoLoad) {
        this.logoLoad = this.data.logo
          ? Promise.race([loadImage(this.data.logo), wait(5000).then(() => null)]).catch(() => null)
          : Promise.resolve(null);
      }
      return this.logoLoad;
    }

    prepareAll() {
      if (this.preparing) return this.preparing;
      const pending = this.items.filter((item) => !this.results.has(item));
      if (!pending.length) {
        this.refresh();
        return Promise.resolve();
      }
      this.setStatus(this.text.preparing);
      const opts = this.renderOptions();
      this.preparing = Promise.all([loadFonts(this.data.fonts), this.loadLogo()])
        .then(async ([, logo]) => {
          opts.logo = logo;
          // One photo at a time keeps memory low on phones; stop if the panel is closed and resume on reopen.
          for (const item of pending) {
            if (!this.dialog.open || !this.dialog.isConnected) break;
            await this.prepare(item, opts);
          }
        })
        .finally(() => {
          this.preparing = null;
          // Announce failures in the panel's live status too (the per-photo chips are not spoken).
          if (!this.saving) this.setStatus(this.failureMessage());
          this.refresh();
        });
      return this.preparing;
    }

    failureMessage() {
      const failed = this.items.filter((item) => item.dataset.state === 'error').length;
      if (!failed) return '';
      if (this.items.length === 1) return this.text.error || '';
      return format(this.text.errors || this.text.error, { n: failed, total: this.items.length });
    }

    async prepare(item, opts) {
      const stateEl = item.querySelector('[data-hom-save-state]');
      const button = item.querySelector('[data-hom-save-one]');
      const preview = item.querySelector('img');
      item.dataset.state = 'loading';
      if (stateEl) {
        stateEl.textContent = this.text.preparing || '';
        stateEl.hidden = false;
      }
      try {
        const blob = await watermark(item.dataset.src, opts);
        const name = `houseofmaryam-${this.data.handle || 'photo'}-${item.dataset.index || this.items.indexOf(item) + 1}.jpg`;
        const url = URL.createObjectURL(blob);
        this.results.set(item, { blob, url, name, file: makeFile(blob, name) });
        if (preview) {
          preview.removeAttribute('srcset');
          preview.src = url;
        }
        item.dataset.state = 'ready';
        if (stateEl) {
          stateEl.textContent = '';
          stateEl.hidden = true;
        }
        if (button) button.disabled = false;
      } catch (error) {
        item.dataset.state = 'error';
        if (stateEl) {
          stateEl.textContent = this.text.error || '';
          stateEl.hidden = false;
        }
      }
    }

    // Save all waits until every photo is ready (or has failed), so the share sheet can open straight from the tap.
    refresh() {
      const settled = this.items.every((item) => item.dataset.state === 'ready' || item.dataset.state === 'error');
      const ready = this.items.filter((item) => this.results.has(item)).length;
      if (this.saveAllButton) this.saveAllButton.disabled = this.saving || !settled || ready === 0;
    }

    async saveOne(item) {
      const result = item && this.results.get(item);
      if (!result) return;
      const data = shareSheetData([result.file], shareDetails(this.dialog));
      if (data) {
        try {
          await navigator.share(data);
          item.dataset.saved = 'true';
          return;
        } catch (error) {
          if (error && error.name === 'AbortError') return;
        }
      }
      download(result);
      // The photo's download button shows a tick from now on.
      item.dataset.saved = 'true';
    }

    async saveAll() {
      if (this.saving) return;
      const ready = this.items.map((item) => this.results.get(item)).filter(Boolean);
      if (!ready.length) return;
      this.saving = true;
      this.refresh();
      try {
        const files = ready.map((result) => result.file);
        const markSaved = () => this.items.forEach((item) => {
          if (this.results.has(item)) item.dataset.saved = 'true';
        });
        const data = shareSheetData(files, shareDetails(this.dialog));
        if (data) {
          try {
            await navigator.share(data);
            markSaved();
            return;
          } catch (error) {
            if (error && error.name === 'AbortError') return;
            // Too many files for the share sheet, or not allowed: fall back to downloads.
          }
        }
        // One by one, never a zip. The pause lets the browser start each download.
        for (let i = 0; i < ready.length; i += 1) {
          if (!this.dialog.isConnected) break;
          this.setStatus(format(this.text.saving, { n: i + 1, total: ready.length }));
          download(ready[i]);
          if (i < ready.length - 1) await wait(900);
        }
        this.setStatus(format(this.text.done, { total: ready.length }));
        markSaved();
      } finally {
        this.saving = false;
        this.refresh();
      }
    }

    destroy() {
      this.results.forEach((result) => URL.revokeObjectURL(result.url));
      this.results.clear();
    }
  }

  const savers = new WeakMap();
  const allSavers = new Set();

  function saverFor(dialog) {
    let saver = savers.get(dialog);
    if (!saver) {
      saver = new SavePhotos(dialog);
      savers.set(dialog, saver);
      allSavers.add(saver);
    }
    return saver;
  }

  function openSave(button) {
    const tools = button.closest('[data-hom-media-overlay]');
    const dialog =
      (tools && tools.querySelector('[data-hom-save-dialog]')) ||
      document.getElementById(button.getAttribute('aria-controls'));
    if (!dialog) return;
    saverFor(dialog).open(button);
  }

  /* ------------------------------------------------------------------
     Wiring
     ------------------------------------------------------------------ */
  const observed = new WeakSet();

  function initOverlay(overlay) {
    if (inQuickView(overlay)) {
      // Zenith's quick view copies the product HTML and strips the gallery extras: the layer goes too (CSS hides it already).
      overlay.remove();
      return;
    }
    initCounter(overlay);

    // Zenith replaces the gallery's inner HTML when variant media display is not "show all" (global.js
    // updateElementsAfterFetch): set the new markup up again. Direct children only; Swiper reorders slides deeper down.
    const media = overlay.closest('.product__media');
    if (media && !observed.has(media) && 'MutationObserver' in window) {
      observed.add(media);
      new MutationObserver(() => scan(media)).observe(media, { childList: true });
    }
  }

  const pendingScans = new WeakSet();

  function scan(root) {
    const scope = root || document;
    if (scope !== document) {
      // Several mutations arrive at once: scan once, shortly after (a timer also runs in background tabs).
      if (pendingScans.has(scope)) return;
      pendingScans.add(scope);
      setTimeout(() => {
        pendingScans.delete(scope);
        scope.querySelectorAll(OVERLAY).forEach(initOverlay);
      }, 30);
      return;
    }
    document.querySelectorAll(OVERLAY).forEach(initOverlay);
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const share = target.closest('[data-hom-share]');
    if (share && !inQuickView(share)) {
      event.preventDefault();
      onShare(share);
      return;
    }
    const save = target.closest('[data-hom-save-open]');
    if (save && !inQuickView(save)) {
      event.preventDefault();
      openSave(save);
    }
  });

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    allSavers.forEach((saver) => {
      if (event.target.contains(saver.dialog)) {
        saver.close();
        saver.destroy();
        allSavers.delete(saver);
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document));
  } else {
    scan(document);
  }

  api.scan = scan;
})();
